import {Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Title} from '@angular/platform-browser';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TablerIconsModule} from 'angular-tabler-icons';
import {Subscription, switchMap, timer} from 'rxjs';
import {MaterialModule} from '@app/material.module';
import {environment} from '@environment/environment';
import {ExplorerEntity, RagStatus} from '@interfaces/explorer-entity';
import {User} from '@interfaces/user';
import {Roles} from '@core/enums/Roles';
import {Router} from '@angular/router';
import {AuthService} from '@services/api/auth/auth.service';
import {FilemanagerApiService} from '@services/api/filemanager/filemanager-api.service';
import {RagApiService, RagChatReference, RagSessionSummary} from '@services/api/rag/rag-api.service';
import {LogoutService} from '@services/api/logout/logout.service';
import {CoreService} from '@services/core.service';
import {UserStorageService} from '@services/local-storage/user-storage/user-storage.service';
import {ModalsConfirmComponent} from '@app/components/modals/modals-confirm/modals-confirm.component';

interface PageRange {
  from: number;
  to: number;
}

interface MessageSource {
  index: number;
  name: string;
  pages: number[];
  /** Pagine consecutive raggruppate: 12–14, 35 */
  ranges: PageRange[];
  fileUuid: string | null;
  available: boolean;
}

interface DocMessage {
  role: 'user' | 'bot';
  text: string;
  /** Risposta del bot già convertita in HTML sicuro (grassetti, elenchi). */
  html?: string;
  timestamp: Date;
  sources?: MessageSource[];
  error?: boolean;
  copied?: boolean;
}

/** Conversazione aperta. La conserva il backend: si riprende dalla cronologia. */
interface DocConversation {
  sessionId: string;
  /** uuid del documento su cui e' la conversazione, null = tutti i documenti pronti */
  scope: string | null;
  messages: DocMessage[];
}

type PanelTab = 'chats' | 'docs';

interface SessionGroup {
  /** Chiave di traduzione: oggi, ieri, ultimi 7 giorni, precedenti */
  label: string;
  items: RagSessionSummary[];
}

const LANG_KEY = 'app_language';

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function inlineMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Citazioni [1] o [1, 2]: numeri cliccabili che aprono la fonte corrispondente
    .replace(/\[(\d+(?:\s*,\s*\d+)*)\]/g, (_, list: string) =>
      list.split(',').map((n) => `<sup class="dm-cite">${n.trim()}</sup>`).join(''));
}

/**
 * Converte il markdown essenziale delle risposte LLM (grassetto, codice, elenchi
 * puntati/numerati, titoletti) in HTML. Il testo viene sempre escapato prima,
 * quindi nessun HTML proveniente dal modello arriva al DOM.
 */
function renderAnswer(answer: string): string {
  const out: string[] = [];
  let list: 'ul' | 'ol' | null = null;
  const closeList = () => {
    if (list) {
      out.push(`</${list}>`);
      list = null;
    }
  };

  for (const raw of escapeHtml(answer ?? '').split('\n')) {
    const line = raw.trim();
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    const heading = line.match(/^#{1,4}\s+(.*)$/);
    if (bullet || numbered) {
      const type = bullet ? 'ul' : 'ol';
      if (list !== type) {
        closeList();
        out.push(`<${type}>`);
        list = type;
      }
      out.push(`<li>${inlineMarkdown((bullet ?? numbered)![1])}</li>`);
    } else if (!line) {
      closeList();
    } else {
      closeList();
      out.push(heading ? `<p><strong>${inlineMarkdown(heading[1])}</strong></p>` : `<p>${inlineMarkdown(line)}</p>`);
    }
  }
  closeList();
  return out.join('');
}

function pageRanges(pages: number[]): PageRange[] {
  const ranges: PageRange[] = [];
  [...new Set(pages)].sort((a, b) => a - b).forEach((page) => {
    const last = ranges[ranges.length - 1];
    if (last && page === last.to + 1) {
      last.to = page;
    } else {
      ranges.push({from: page, to: page});
    }
  });
  return ranges;
}

/** Fonti della risposta, nell'ordine dei numeri citati nel testo. */
function toSources(references?: RagChatReference[]): MessageSource[] {
  return (references ?? []).map((r, i) => {
    const pages = r.pages?.length ? r.pages : (r.page != null ? [r.page] : []);
    return {
      index: r.index ?? i + 1,
      name: r.source,
      pages,
      ranges: pageRanges(pages),
      fileUuid: r.file_uuid ?? null,
      available: r.available ?? !!r.file_uuid,
    };
  });
}

/**
 * Assistente DM Manufacturing (front office): si fanno domande sui documenti dell'azienda,
 * pronti e resi disponibili all'AI dal back office. Le conversazioni restano nella cronologia.
 */
@Component({
  selector: 'app-documents',
  standalone: true,
  imports: [CommonModule, DatePipe, FormsModule, MaterialModule, TablerIconsModule, TranslateModule],
  templateUrl: './documents.component.html',
  styleUrl: './documents.component.scss',
})
export class DocumentsComponent implements OnInit, OnDestroy {
  /** Intervallo tra due letture di stato durante l'indicizzazione. */
  private readonly POLL_INTERVAL_MS = 4000;

  readonly RagStatus = RagStatus;
  readonly languages = environment.language_list;
  readonly suggestions = ['documents.suggestion_summary', 'documents.suggestion_key_points', 'documents.suggestion_safety'];

  documents: ExplorerEntity[] = [];
  listLoaded = false;
  tab: PanelTab = 'chats';
  sessions: RagSessionSummary[] = [];
  sessionsLoaded = false;
  sessionFilter = '';
  /** Pannello laterale su smartphone (a scomparsa) */
  sidebarOpen = false;
  current: DocConversation = this.freshConversation(null);
  loadingConversation = false;

  inputText = '';
  sending = false;
  user: User | null = null;
  currentLanguage = environment.language_default;
  /** Blob URL dei PDF aperti in un'altra scheda, revocati all'uscita dalla pagina. */
  private pdfUrls: string[] = [];

  private filemanagerApi = inject(FilemanagerApiService);
  private ragApi = inject(RagApiService);
  private coreService = inject(CoreService);
  private translate = inject(TranslateService);
  private titleService = inject(Title);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private logoutService = inject(LogoutService);
  private userStorage = inject(UserStorageService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private pollSub?: Subscription;
  private userSub?: Subscription;
  private langSub?: Subscription;

  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('chatInput') chatInput?: ElementRef<HTMLTextAreaElement>;

  ngOnInit(): void {
    // Questa pagina non usa l'header del vecchio layout: la lingua salvata va ripristinata qui
    const saved = localStorage.getItem(LANG_KEY);
    if (saved && this.languages.some((l) => l.code === saved)) {
      this.setLanguage(saved);
    } else {
      // Senza preferenza salvata si usa la lingua di default dell'app (senza memorizzarla)
      this.currentLanguage = this.translate.currentLang || environment.language_default;
      this.translate.use(this.currentLanguage);
      this.coreService.setLanguage(this.currentLanguage);
    }
    // stream() riemette quando le traduzioni arrivano e a ogni cambio lingua
    this.langSub = this.translate.stream('documents.title').subscribe(
      (title: string) => this.titleService.setTitle(title + environment.second_title),
    );

    this.userSub = this.userStorage.user$.subscribe((user) => (this.user = user));
    this.userStorage.getUserStorage().then((user) => (this.user = this.user ?? user));

    this.loadDocuments();
    this.loadSessions();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.userSub?.unsubscribe();
    this.langSub?.unsubscribe();
    this.pdfUrls.forEach((url) => URL.revokeObjectURL(url));
  }

  // ---------------------------------------------------------------------------
  // Stato derivato
  // ---------------------------------------------------------------------------

  get readyDocuments(): ExplorerEntity[] {
    return this.documents.filter((d) => this.isReady(d));
  }

  get selectedDocument(): ExplorerEntity | null {
    const scope = this.current.scope;
    return scope ? this.documents.find((d) => d.uuid === scope) ?? null : null;
  }

  /** Vero se l'ambito della conversazione ha almeno un documento interrogabile. */
  get canChat(): boolean {
    if (this.current.scope) {
      const doc = this.selectedDocument;
      return !!doc && this.isReady(doc);
    }
    return this.readyDocuments.length > 0;
  }

  get hasPendingDocuments(): boolean {
    return this.documents.some((d) => d.rag_status === RagStatus.PENDING);
  }

  get messages(): DocMessage[] {
    return this.current.messages;
  }

  get userInitials(): string {
    const name = `${this.user?.name ?? ''} ${this.user?.lastname ?? ''}`.trim();
    const source = name || this.user?.email || '?';
    return source.split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  }

  isReady(doc: ExplorerEntity): boolean {
    return doc.rag_status === RagStatus.ANALYZED && !!doc.rag_document_id;
  }

  // ---------------------------------------------------------------------------
  // Documenti (solo consultazione: si gestiscono dal back office)
  // ---------------------------------------------------------------------------

  private loadDocuments(loaderShow = true): void {
    this.filemanagerApi.getAllDocuments('-created_at', loaderShow).subscribe({
      next: (response) => {
        this.applyList((response?.data ?? []) as ExplorerEntity[]);
        this.listLoaded = true;
      },
      error: () => {
        this.listLoaded = true;
        this.showError('documents.error_load');
      },
    });
  }

  private applyList(items: ExplorerEntity[]): void {
    // L'assistente usa solo i documenti disponibili all'AI: esclusi e in eliminazione non si mostrano
    this.documents = items.filter((it) => it.type === 'file'
      && ![RagStatus.EXCLUDED, RagStatus.DELETING].includes(it.rag_status as RagStatus));
    this.syncPolling();
  }

  /** Nuova conversazione sul documento scelto (o su tutti). */
  selectScope(scope: string | null): void {
    this.sidebarOpen = false;
    if (this.current.scope === scope && !this.current.messages.length) {
      return;
    }
    this.current = this.freshConversation(scope);
    this.inputText = '';
  }

  /** Polling silenzioso solo finché c'è almeno un documento in indicizzazione. */
  private syncPolling(): void {
    if (!this.hasPendingDocuments) {
      this.stopPolling();
      return;
    }
    if (this.pollSub) {
      return;
    }
    this.pollSub = timer(this.POLL_INTERVAL_MS, this.POLL_INTERVAL_MS)
      .pipe(switchMap(() => this.filemanagerApi.getAllDocuments('-created_at', false)))
      .subscribe({
        next: (response) => this.applyList((response?.data ?? []) as ExplorerEntity[]),
        error: () => this.stopPolling(),
      });
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  // ---------------------------------------------------------------------------
  // Cronologia delle conversazioni
  // ---------------------------------------------------------------------------

  private freshConversation(scope: string | null): DocConversation {
    return {sessionId: crypto.randomUUID(), scope, messages: []};
  }

  private loadSessions(): void {
    this.ragApi.sessions().subscribe({
      next: (sessions) => {
        this.sessions = sessions;
        this.sessionsLoaded = true;
      },
      error: () => (this.sessionsLoaded = true),
    });
  }

  /** Conversazioni filtrate per titolo e raggruppate per ultima attivita'. */
  get sessionGroups(): SessionGroup[] {
    const term = this.sessionFilter.trim().toLowerCase();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const day = 24 * 60 * 60 * 1000;
    const groups: SessionGroup[] = [
      {label: 'documents.group_today', items: []},
      {label: 'documents.group_yesterday', items: []},
      {label: 'documents.group_week', items: []},
      {label: 'documents.group_older', items: []},
    ];
    this.sessions
      .filter((s) => !term || `${s.title} ${s.scope_name ?? ''}`.toLowerCase().includes(term))
      .forEach((s) => {
        const at = new Date(s.updated_at).getTime();
        const group = at >= startOfToday.getTime() ? 0
          : at >= startOfToday.getTime() - day ? 1
          : at >= startOfToday.getTime() - 6 * day ? 2 : 3;
        groups[group].items.push(s);
      });
    return groups.filter((g) => g.items.length);
  }

  isCurrent(session: RagSessionSummary): boolean {
    return session.session_id === this.current.sessionId;
  }

  openSession(session: RagSessionSummary): void {
    this.sidebarOpen = false;
    if (this.isCurrent(session) || this.sending) {
      return;
    }
    this.loadingConversation = true;
    this.ragApi.session(session.session_id).subscribe({
      next: (detail) => {
        const messages: DocMessage[] = [];
        detail.messages.forEach((m) => {
          messages.push({role: 'user', text: m.user_message, timestamp: new Date(m.user_message_at)});
          if (m.ai_answer) {
            messages.push({
              role: 'bot', text: m.ai_answer, html: renderAnswer(m.ai_answer),
              timestamp: new Date(m.ai_answer_at ?? m.user_message_at), sources: toSources(m.references),
            });
          }
        });
        this.current = {sessionId: detail.session_id, scope: detail.scope, messages};
        this.loadingConversation = false;
        this.scrollToBottom();
      },
      error: () => {
        this.loadingConversation = false;
        this.showError('documents.error_history');
      },
    });
  }

  deleteSession(session: RagSessionSummary, event: Event): void {
    event.stopPropagation();
    this.dialog.open(ModalsConfirmComponent, {
      panelClass: 'dm-admin-dialog',
      data: {
        title: 'documents.delete_chat_title',
        message: 'documents.delete_chat_message',
        confirmText: 'documents.delete_confirm',
        cancelText: 'documents.cancel',
      },
    }).afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.ragApi.deleteSession(session.session_id).subscribe({
        next: () => {
          if (this.isCurrent(session)) {
            this.current = this.freshConversation(null);
          }
          this.sessions = this.sessions.filter((s) => s.session_id !== session.session_id);
        },
        error: () => this.showError('documents.error_history'),
      });
    });
  }

  newConversation(): void {
    this.current = this.freshConversation(null);
    this.inputText = '';
    this.sidebarOpen = false;
    setTimeout(() => this.chatInput?.nativeElement.focus());
  }

  // ---------------------------------------------------------------------------
  // Chat
  // ---------------------------------------------------------------------------

  useSuggestion(key: string): void {
    this.inputText = this.translate.instant(key);
    this.sendMessage();
  }

  onEnterKey(event: Event): void {
    event.preventDefault();
    this.sendMessage();
  }

  sendMessage(): void {
    const text = this.inputText.trim();
    if (!text || this.sending || !this.canChat) {
      return;
    }
    const conv = this.current;
    conv.messages.push({role: 'user', text, timestamp: new Date()});
    this.inputText = '';
    // Anche il campo stesso: se si scrive subito dopo "Nuova conversazione" ngModel puo' non aggiornarlo in tempo
    if (this.chatInput) {
      this.chatInput.nativeElement.value = '';
    }
    this.sending = true;
    this.resetInputHeight();
    this.scrollToBottom();

    // Azienda e documenti ammessi li decide il backend dall'utente autenticato
    this.ragApi.chat({
      session_id: conv.sessionId,
      message: text,
      language: this.currentLanguage.toUpperCase(),
      scope: conv.scope,
    }).subscribe({
      next: (res) => {
        conv.messages.push({
          role: 'bot',
          text: res.data.answer,
          html: renderAnswer(res.data.answer),
          timestamp: new Date(),
          sources: toSources(res.data.references),
        });
        this.sending = false;
        this.scrollToBottom();
        this.loadSessions();
      },
      error: () => {
        conv.messages.push({role: 'bot', text: this.translate.instant('documents.error_chat'), timestamp: new Date(), error: true});
        this.sending = false;
        this.scrollToBottom();
      },
    });
  }

  copyAnswer(msg: DocMessage): void {
    navigator.clipboard?.writeText(msg.text).then(() => {
      msg.copied = true;
      setTimeout(() => (msg.copied = false), 2000);
    });
  }

  /** Apre la fonte citata: il PDF del documento (per uuid), alla pagina indicata. */
  openSource(source: MessageSource, page: number): void {
    if (!source.available || !source.fileUuid) {
      this.showError('documents.source_unavailable');
      return;
    }
    this.openPdf({uuid: source.fileUuid} as ExplorerEntity, page);
  }

  /** Clic su una citazione [n] nel testo della risposta: apre quella fonte alla prima pagina citata. */
  onAnswerClick(event: MouseEvent, msg: DocMessage): void {
    const cite = (event.target as HTMLElement | null)?.closest('.dm-cite');
    if (!cite) {
      return;
    }
    const source = msg.sources?.find((s) => s.index === Number(cite.textContent));
    if (source) {
      this.openSource(source, source.pages[0]);
    }
  }

  /**
   * Scarica il PDF decifrato dal backend e lo apre in una nuova scheda.
   * La scheda va aperta subito (nel gesto dell'utente) o il browser la blocca come popup.
   */
  openPdf(doc: ExplorerEntity, page?: number, event?: Event): void {
    event?.stopPropagation();
    if (!doc.uuid) {
      return;
    }
    const win = window.open('', '_blank');
    this.filemanagerApi.getFileBytes(doc.uuid).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(new Blob([blob as unknown as Blob], {type: 'application/pdf'}));
        this.pdfUrls.push(url);
        const target = page ? `${url}#page=${page}` : url;
        if (win) {
          win.location.href = target;
        } else {
          window.open(target, '_blank');
        }
      },
      error: () => win?.close(),
    });
  }

  formatSize(bytes?: number | null): string {
    if (!bytes) {
      return '';
    }
    return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  /** Adatta l'altezza dell'input al contenuto (max ~6 righe, poi scrolla). */
  autoGrowInput(): void {
    const el = this.chatInput?.nativeElement;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }

  private resetInputHeight(): void {
    setTimeout(() => {
      const el = this.chatInput?.nativeElement;
      if (el) {
        el.style.height = 'auto';
      }
    });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }

  // ---------------------------------------------------------------------------
  // Utente e lingua
  // ---------------------------------------------------------------------------

  setLanguage(code: string): void {
    this.translate.use(code);
    this.coreService.setLanguage(code);
    localStorage.setItem(LANG_KEY, code);
    this.currentLanguage = code;
  }

  /** Amministratori e gestori documenti gestiscono i documenti dal back office; i dipendenti consultano. */
  get canAdminister(): boolean {
    return !!this.user && this.user.roles?.id !== Roles.TECHNICIAN;
  }

  openAdmin(): void {
    this.router.navigate(['/admin']);
  }

  openDocumentsAdmin(): void {
    this.router.navigate(['/admin/documents']);
  }

  logout(): void {
    // Invalida il token anche sul server, poi pulisce i dati locali in ogni caso
    this.authService.logout().subscribe({
      complete: () => this.logoutService.logout(),
      error: () => this.logoutService.logout(),
    });
  }

  private showError(key: string): void {
    this.snackBar.open(this.translate.instant(key), this.translate.instant('documents.close'), {duration: 5000});
  }
}
